import { useState, useEffect, useCallback, useRef } from 'react'
import { Bell, X, CheckCheck, Trash2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ro, enUS, zhCN } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useNotifications } from '../hooks/useActivityFeed'
import { useAuth } from '../contexts/AuthContext'
import { useAchievement } from '../contexts/AchievementContext'
import DangerDialog from './ui/DangerDialog'
import EmptyState from './ui/EmptyState'
import { playSound } from '../lib/sounds'

const DATE_LOCALES = { ro, en: enUS, zh: zhCN }

function formatNotificationTime(value, locale) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return formatDistanceToNow(date, { addSuffix: true, locale })
}

export default function NotificationBell({ onOpenCard }) {
    const { t, i18n } = useTranslation()
    const dateLocale = DATE_LOCALES[i18n.language] || ro
    const { user } = useAuth()
    const { showAchievement } = useAchievement()
    const {
        notifications,
        unreadCount,
        markRead,
        markAllRead,
        deleteNotification,
        deleteAll,
    } = useNotifications(user?.id)
    const [open, setOpen] = useState(false)
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false)
    const [busyAction, setBusyAction] = useState('')
    const seenNotificationIdsRef = useRef(new Set())
    const initializedNotificationsRef = useRef(false)

    useEffect(() => {
        if (!open) return
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [open])

    const handleOpenNotification = useCallback(async (notification) => {
        if (!notification?.id) return
        if (!notification.is_read) {
            await markRead(notification.id)
        }

        if (notification.card_id && onOpenCard) {
            setOpen(false)
            await onOpenCard(notification.card_id, { preload: true })
        }
    }, [markRead, onOpenCard])

    useEffect(() => {
        if (!user?.id) {
            seenNotificationIdsRef.current = new Set()
            initializedNotificationsRef.current = false
            return
        }

        const currentIds = new Set(notifications.map((notification) => notification.id).filter(Boolean))
        if (!initializedNotificationsRef.current) {
            seenNotificationIdsRef.current = currentIds
            initializedNotificationsRef.current = true
            return
        }

        const freshUnread = notifications.filter((notification) => (
            notification?.id &&
            !notification.is_read &&
            !seenNotificationIdsRef.current.has(notification.id)
        ))
        seenNotificationIdsRef.current = currentIds

        // Small ding when genuinely-new unread notifications arrive (never on
        // the initial seed — that returns early above). Respects the sound
        // toggle (playSound reads kanbify_sounds).
        if (freshUnread.length) playSound('notify')

        for (const notification of freshUnread.slice().reverse()) {
            const title = notification.title || t('notifications.title')
            const bodyText = notification.body || notification.message || ''
            showAchievement({
                type: 'notification',
                title,
                message: bodyText || title,
                duration: 6500,
                onClick: () => { void handleOpenNotification(notification) },
            })
        }
    }, [handleOpenNotification, notifications, showAchievement, t, user?.id])

    const handleMarkAllRead = async () => {
        setBusyAction('mark-all')
        await markAllRead()
        setBusyAction('')
    }

    const handleDeleteAll = async () => {
        setBusyAction('delete-all')
        await deleteAll()
        setBusyAction('')
        setConfirmDeleteAll(false)
    }

    const handleDeleteOne = async (event, notificationId) => {
        event.stopPropagation()
        await deleteNotification(notificationId)
    }

    const handleNotificationKeyDown = (event, notification) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            void handleOpenNotification(notification)
        }
    }

    const visibleNotifications = notifications.filter((notification) => notification?.id)

    return (
        <>
            <div className="notif-wrapper">
                <button
                    type="button"
                    className="notif-bell-btn"
                    onClick={() => setOpen((value) => !value)}
                    aria-expanded={open}
                    aria-label={t('notifications.openPanel')}
                >
                    <Bell size={18} />
                    {unreadCount > 0 ? (
                        <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    ) : null}
                </button>

                {open ? (
                    <>
                        <div className="notif-backdrop" onClick={() => setOpen(false)} />

                        <section className="notif-panel" aria-label={t('notifications.panelLabel')}>
                            <div className="notif-header">
                                <div className="notif-title-group">
                                    <span className="notif-title">{t('notifications.title')}</span>
                                    {visibleNotifications.length > 0 ? (
                                        <span className="notif-count">({visibleNotifications.length})</span>
                                    ) : null}
                                </div>

                                <div className="notif-actions">
                                    {unreadCount > 0 ? (
                                        <button
                                            type="button"
                                            className="notif-mark-all"
                                            onClick={handleMarkAllRead}
                                            disabled={busyAction !== ''}
                                            title={t('notifications.markAllReadTitle')}
                                        >
                                            <CheckCheck size={13} /> {t('notifications.markAllRead')}
                                        </button>
                                    ) : null}

                                    {visibleNotifications.length > 0 ? (
                                        <button
                                            type="button"
                                            className="notif-mark-all danger"
                                            onClick={() => setConfirmDeleteAll(true)}
                                            disabled={busyAction !== ''}
                                            title={t('notifications.deleteAllTitle')}
                                        >
                                            <Trash2 size={13} /> {t('notifications.deleteAll')}
                                        </button>
                                    ) : null}

                                    <button
                                        type="button"
                                        className="notif-close"
                                        onClick={() => setOpen(false)}
                                        aria-label={t('notifications.closePanel')}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="notif-list">
                                {visibleNotifications.length === 0 ? (
                                    <EmptyState
                                        icon={<Bell size={40} style={{ color: 'var(--text-muted)', opacity: 0.45 }} />}
                                        title={t('notifications.emptyTitle')}
                                        description={t('notifications.emptyDescription')}
                                    />
                                ) : (
                                    visibleNotifications.map((notification) => {
                                        const title = notification.title || t('notifications.title')
                                        const bodyText = notification.body || notification.message || ''
                                        const timeLabel = formatNotificationTime(notification.created_at, dateLocale)
                                        return (
                                        <div
                                            key={notification.id}
                                            role="button"
                                            tabIndex={0}
                                            className={`notif-item ${!notification.is_read ? 'unread' : ''}`.trim()}
                                            onClick={() => handleOpenNotification(notification)}
                                            onKeyDown={(event) => handleNotificationKeyDown(event, notification)}
                                        >
                                            <div className="notif-icon">{notification.icon || '!'}</div>

                                            <div className="notif-content">
                                                <div className="notif-item-title">{title}</div>
                                                {bodyText ? (
                                                    <div className="notif-item-body">{bodyText}</div>
                                                ) : null}
                                                {timeLabel ? <div className="notif-item-time">{timeLabel}</div> : null}
                                            </div>

                                            <div className="notif-item-side">
                                                {!notification.is_read ? <div className="notif-dot" /> : <span className="notif-read-label">{t('notifications.read')}</span>}
                                                <button
                                                    type="button"
                                                    className="notif-delete-btn"
                                                    onClick={(event) => handleDeleteOne(event, notification.id)}
                                                    aria-label={t('notifications.deleteOne', { title })}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        </div>
                                        )
                                    })
                                )}
                            </div>
                        </section>
                    </>
                ) : null}
            </div>

            <DangerDialog
                open={confirmDeleteAll}
                title={t('notifications.deleteAllConfirmTitle')}
                description={t('notifications.deleteAllConfirmDescription')}
                confirmLabel={t('notifications.deleteAllConfirm')}
                loading={busyAction === 'delete-all'}
                onClose={() => setConfirmDeleteAll(false)}
                onConfirm={handleDeleteAll}
            />
        </>
    )
}
